import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MasterService } from 'app/service/master.service';
import { FssaiGeneratedTagService } from 'app/service/fssai-generated-tag.service';
import { from, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { extractMetadataOptions } from '../assigner-bulk-upload/assigner-bulk-upload.util';
import { resolveFssaiStateFromFormOption } from 'app/shared/metadata/fssai-product-form.util';
import { STANDARD_MACRO_LABELS } from 'app/shared/metadata/micro-nutrition.util';
import {
  SUP_BULK_NET_QTY_UNITS,
  SUP_BULK_SHELF_LIFE_UNITS,
  SUP_BULK_STATUS_OPTIONS,
  SupBulkClaimDraft,
  SupBulkEntitySelections,
  SupBulkHealthConditionGroup,
  SupBulkLookups,
  SupBulkLookupOption,
  SupBulkNutrientItem,
  SupBulkPreviewRow,
  SupBulkResolvedIngredient,
  SupBulkSkuDraft,
} from './super-admin-supplement-bulk-upload.models';
import { NutraceuticalIngredientMasterService } from 'app/service/nutraceutical-ingredient-master.service';
import { getSupBulkFieldErrors, rebuildSupplementBulkPayload } from './super-admin-supplement-bulk-upload.util';

/** Local type for leftover UOM helpers (UOM tab hidden for supplements). */
type SupBulkServingMeasurement = any;

interface FpPincodeLookupItem {
  id: string;
  pincodeId: string;
  pincode: string;
  label: string;
  name: string;
  area?: string;
  cityId: string;
  cityName: string;
  stateId: string;
  stateName: string;
  countryId: string;
  countryName: string;
  isLookup?: boolean;
}

@Component({
  selector: 'app-super-admin-supplement-bulk-upload-row-editor',
  templateUrl: './super-admin-supplement-bulk-upload-row-editor.component.html',
  styleUrls: ['./super-admin-supplement-bulk-upload-row-editor.component.scss'],
})
export class SuperAdminSupplementBulkUploadRowEditorComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() row!: SupBulkPreviewRow;
  @Input() lookups!: SupBulkLookups;

  activeTab = 1;
  fieldErrors: Record<string, string> = {};
  sectionErrorMessages: string[] = [];

  readonly editorTabs: Array<{ id: number; short: string; label: string }> = [
    { id: 1, short: 'Company', label: '1. Company Details' },
    { id: 2, short: 'Product', label: '2. Product Details' },
    { id: 3, short: 'Entities', label: '3. Manufactured / Marketed / Packaged' },
    { id: 4, short: 'SKU', label: '4. Other SKU Details' },
    { id: 5, short: 'Categories+', label: '5. FSSAI & E-Store + Sensory' },
    { id: 6, short: 'Ingredients', label: '6. Ingredients' },
    { id: 7, short: 'Dosage', label: '7. Recommended Dosage' },
    { id: 8, short: 'Allergens', label: '8. Allergen & Intolerance + Observed' },
    { id: 9, short: 'Nutrients', label: '9. Nutrient Information' },
    { id: 10, short: 'Claims', label: '10. Claims / Benefits / Ingredient Claims' },
    { id: 11, short: 'Auth', label: '11. Auth Certificate + License' },
    { id: 12, short: 'Directions+', label: '12. Directions / Warnings / Disclaimer' },
    { id: 13, short: 'Status', label: '13. Status' },
  ];

  get activeTabLabel(): string {
    return this.editorTabs.find((t) => t.id === this.activeTab)?.label || '';
  }

  readonly section3EntityDefs = [
    { key: 'manufacturedBy' as const, label: 'Manufactured by', nameKey: 'manufacturedByName', addressKey: 'manufacturedByAddress', licenseKey: 'manufacturedByLicenseNo' },
    { key: 'marketedBy' as const, label: 'Marketed by', nameKey: 'marketedByName', addressKey: 'marketedByAddress', licenseKey: 'marketedByLicenseNo' },
    { key: 'packagedBy' as const, label: 'Packaged by', nameKey: 'packagedByName', addressKey: 'packagedByAddress', licenseKey: 'packagedByLicenseNo' },
  ];

    readonly statusOptions = SUP_BULK_STATUS_OPTIONS.map((s) => ({ value: s, label: s }));
  readonly shelfLifeUnits = SUP_BULK_SHELF_LIFE_UNITS;
  readonly netQtyUnits = SUP_BULK_NET_QTY_UNITS;
  readonly ratingOptions = [
    { value: 1, label: 'Poor' },
    { value: 2, label: 'Fair' },
    { value: 3, label: 'Good' },
    { value: 4, label: 'Very Good' },
    { value: 5, label: 'Excellent' },
  ];

  companyStates: SupBulkLookupOption[] = [];
  companyCities: SupBulkLookupOption[] = [];
  /** City-filtered OR pincode-search results (with hierarchy). */
  companyPincodeItems: FpPincodeLookupItem[] = [];
  loadingCompanyStates = false;
  loadingCompanyCities = false;
  loadingCompanyPincodes = false;
  searchingCompanyPincode = false;

  companyPincodeTypeahead = new Subject<string>();
  private companyPincodeTypeaheadSub?: Subscription;
  private pincodeLookupCache = new Map<string, FpPincodeLookupItem>();
  private applyingPincode = false;

  entityStateOptions: Record<string, SupBulkLookupOption[]> = {};
  entityCityOptions: Record<string, SupBulkLookupOption[]> = {};
  entityPincodeItems: Record<string, FpPincodeLookupItem[]> = {};
  entityPincodeTypeahead: Record<string, Subject<string>> = {};
  entitySearchingPincode: Record<string, boolean> = {};
  private entityPincodeSubs: Subscription[] = [];

  readonly entityKeys: Array<{
    key: keyof Pick<
      SupBulkPreviewRow['selections'],
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
    >;
    label: string;
  }> = [
    { key: 'manufacturedBy', label: 'Manufactured by' },
    { key: 'marketedBy', label: 'Marketed by' },
    { key: 'packagedBy', label: 'Packaged by' },
  ];

  @ViewChild('ingredientDialogTpl') ingredientDialogTpl!: TemplateRef<unknown>;
  @ViewChild('healthGroupModalTpl') healthGroupModalTpl!: TemplateRef<unknown>;
  @ViewChild('uomDialogTpl') uomDialogTpl!: TemplateRef<unknown>;
  ingredientEditIndex: number | null = null;
  ingredientSearchResults: Array<{ id: number; name: string }> = [];
  selectedIngredientId: number | null = null;
  selectedIngredientName = '';
  ingredientServingUnits: Array<{
    servingUnitMasterId: number | string;
    displayLabel: string;
    valueInGram?: number;
  }> = [];
  selectedServingUnitId: number | string | null = null;
  ingredientAmount: number | null = null;
  ingredientSearchLoading = false;
  ingredientTypeahead = new Subject<string>();
  private ingredientTypeaheadSub?: Subscription;
  private ingredientModalRef: NgbModalRef | null = null;
  private healthModalRef: NgbModalRef | null = null;
  private uomModalRef: NgbModalRef | null = null;

  readonly standardMacroLabels = [...STANDARD_MACRO_LABELS];
  standardMacroOptions: Array<{ value: string; label: string }> = this.standardMacroLabels.map((l) => ({
    value: l,
    label: l,
  }));
  selectedMacroToAdd: string | null = null;
  loadingFssaiTags = false;
  private fssaiTagTimer: ReturnType<typeof setTimeout> | null = null;

  healthGroupSearch = '';
  healthModalSearch = '';
  healthModalSelection = new Set<string>();
  private healthExpanded = new Set<string>();

  productUomCategories: Array<{ id: number; name: string; selected: boolean; adding: boolean }> = [];
  productUomLoading = false;
  selectedUomFilter: string | null = null;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private masterService: MasterService,
    private fssaiTagService: FssaiGeneratedTagService,
    private nutraceuticalService: NutraceuticalIngredientMasterService,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef
  ) {}

  get parsed() {
    return this.row.parsed;
  }

  get selections() {
    return this.row.selections;
  }

  async ngOnInit(): Promise<void> {
    this.initCompanyPincodeTypeahead();
    this.initEntityPincodeTypeaheads();
    this.initIngredientTypeahead();
    this.ensureExtendedSelectionDefaults();
    this.normalizeMultiSelectIds();

    await this.hydrateCompanyLocationFromCsvHints();
    for (const e of this.entityKeys) {
      await this.hydrateEntityLocationFromCsvHints(e.key);
    }
    this.scheduleLucideIconRender();
  }

  ngAfterViewInit(): void {
    this.scheduleLucideIconRender();
  }

  ngOnDestroy(): void {
    this.companyPincodeTypeaheadSub?.unsubscribe();
    this.ingredientTypeaheadSub?.unsubscribe();
    this.entityPincodeSubs.forEach((s) => s.unsubscribe());
    this.companyPincodeTypeahead.complete();
    this.ingredientTypeahead.complete();
    Object.values(this.entityPincodeTypeahead).forEach((s) => s.complete());
    try {
      this.ingredientModalRef?.dismiss();
    } catch {
      /* noop */
    }
    try {
      this.uomModalRef?.dismiss();
    } catch {
      /* noop */
    }
    try {
      this.healthModalRef?.dismiss();
    } catch {
      /* noop */
    }
  }

  setTab(tab: number): void {
    this.activeTab = tab;
    this.refreshSectionErrorBanner();
    if (tab === 9) this.scheduleFssaiTagRefresh();
    this.scheduleLucideIconRender();
  }

  private ensureExtendedSelectionDefaults(): void {
    if (!Array.isArray(this.selections.healthConditionTags)) this.selections.healthConditionTags = [];
    if (!Array.isArray(this.selections.generatedFssaiNutrientTags)) {
      this.selections.generatedFssaiNutrientTags = [];
    }
    if (!Array.isArray(this.selections.productClaims)) this.selections.productClaims = [];
    if (!Array.isArray(this.selections.productBenefitClaims)) this.selections.productBenefitClaims = [];
    if (!Array.isArray(this.selections.micronutrients)) this.selections.micronutrients = [];
    this.refreshClaimAvailableTags('product');
    this.refreshClaimAvailableTags('benefit');
    this.refreshMacroOptions();
  }

  /** Keep ng-select bindValue comparisons stable across string/number ids. */
  compareById = (a: unknown, b: unknown): boolean => String(a ?? '') === String(b ?? '');

  /** Resolve chip label when ng-select receives a raw id (avoids blank allergen chips). */
  resolveOptionLabel(
    options: Array<{ id: string | number; name: string }> | undefined,
    item: unknown
  ): string {
    if (item == null) return '';
    if (typeof item === 'object') {
      const obj = item as { name?: string; label?: string; id?: unknown };
      if (obj.name) return String(obj.name);
      if (obj.label) return String(obj.label);
      const match = (options || []).find((o) => String(o.id) === String(obj.id ?? ''));
      return match?.name || String(obj.id ?? '');
    }
    const match = (options || []).find((o) => String(o.id) === String(item));
    return match?.name || String(item);
  }

  /** Keep multi-select models as string ids so chips always match lookup items. */
  private normalizeMultiSelectIds(): void {
    const toIds = (values: Array<string | number> | null | undefined): string[] =>
      (values || [])
        .map((v) => {
          if (v != null && typeof v === 'object' && 'id' in (v as object)) {
            return String((v as { id: unknown }).id ?? '');
          }
          return String(v ?? '').trim();
        })
        .filter(Boolean);

    const s = this.selections;
    s.tasteIds = toIds(s.tasteIds);
    s.flavourIds = toIds(s.flavourIds);
    s.allergenDeclaredIds = toIds(s.allergenDeclaredIds);
    s.mayContainIds = toIds(s.mayContainIds);
    s.producedInFacilityIds = toIds(s.producedInFacilityIds);
    s.allergenContainsIds = toIds(s.allergenContainsIds);
    s.allergenFreeIds = toIds(s.allergenFreeIds);
    s.intoleranceContainsIds = toIds(s.intoleranceContainsIds);
    s.intoleranceFreeIds = toIds(s.intoleranceFreeIds);
  }

  get availableMacroOptions(): Array<{ value: string; label: string }> {
    return this.standardMacroOptions;
  }

  private refreshMacroOptions(): void {
    const used = new Set(
      (this.selections.macronutrients || []).map((m) => String(m.nutrientName || '').toLowerCase())
    );
    this.standardMacroOptions = this.standardMacroLabels
      .filter((l) => !used.has(l.toLowerCase()))
      .map((l) => ({ value: l, label: l }));
  }

  onAddStandardMacro(label: string | { value?: string; label?: string } | null): void {
    const name =
      typeof label === 'string'
        ? label
        : String(label?.value || label?.label || '').trim();
    if (!name) return;
    const needle = name.toLowerCase();
    const nutrient =
      (this.lookups.nutrients || []).find(
        (n) => String(n.name || '').trim().toLowerCase() === needle
      ) ||
      (this.lookups.nutrients || []).find((n) => {
        const optionName = String(n.name || '').trim().toLowerCase();
        return optionName.includes(needle) || needle.includes(optionName);
      });
    this.selections.macronutrients.push({
      nutrientId: nutrient ? String(nutrient.id) : '',
      nutrientName: nutrient?.name || name,
      unitId: '',
      unitName: '',
      amount: '',
      percentDV: '',
      rda: '',
    });
    this.selectedMacroToAdd = null;
    this.refreshMacroOptions();
    this.scheduleFssaiTagRefresh();
    this.cdRef.detectChanges();
  }

  onMacroUnitChange(index: number): void {
    const row = this.selections.macronutrients[index];
    if (!row) return;
    const match = (this.nutrientUnitOptions || []).find((u) => String(u.id) === String(row.unitId));
    row.unitName = match?.name || '';
    this.scheduleFssaiTagRefresh();
  }

  scheduleFssaiTagRefresh(): void {
    if (this.fssaiTagTimer) clearTimeout(this.fssaiTagTimer);
    this.fssaiTagTimer = setTimeout(() => void this.refreshFssaiTags(), 400);
  }

  private async refreshFssaiTags(): Promise<void> {
    const formOption = (this.lookups.supplementForms || []).find(
      (f) => String(f.id) === String(this.selections.productForm)
    );
    const stateOfProduct = resolveFssaiStateFromFormOption(formOption);
    const macros = this.selections.macronutrients || [];
    const findAmt = (name: string) => {
      const row = macros.find((m) => String(m.nutrientName || '').toLowerCase().includes(name));
      const n = Number(row?.amount);
      return Number.isFinite(n) ? n : undefined;
    };
    const calories = findAmt('energy') ?? findAmt('calor');
    const protein = findAmt('protein');
    const fat = findAmt('fat');
    const fiber = findAmt('fiber') ?? findAmt('fibre');
    const carbs = findAmt('carb');
    if (!stateOfProduct || calories == null || protein == null || fat == null || fiber == null) {
      this.selections.generatedFssaiNutrientTags = [];
      this.cdRef.markForCheck();
      return;
    }
    this.loadingFssaiTags = true;
    this.cdRef.markForCheck();
    try {
      const res = await this.fssaiTagService
        .calculateTags({
          stateOfProduct,
          calories,
          protein,
          fat,
          fiber,
          carbs,
        })
        .toPromise();
      this.selections.generatedFssaiNutrientTags = this.fssaiTagService
        .extractTagsFromApiResponse(res)
        .map((t) => ({
          fssaiTagName: t.fssaiTagName || '',
          fssaiNutrientTagMasterId: t.fssaiNutrientTagMasterId,
        }))
        .filter((t) => !!t.fssaiTagName);
    } catch {
      this.selections.generatedFssaiNutrientTags = [];
    } finally {
      this.loadingFssaiTags = false;
      this.cdRef.markForCheck();
    }
  }

  /** Units for nutrient amount fields — prefer item UOMs (g, mg, kcal); fall back to dosage masters. */
  get nutrientUnitOptions(): SupBulkLookupOption[] {
    return this.lookups.units?.length ? this.lookups.units : this.lookups.dosageUnits || [];
  }

  get filteredHealthGroups(): SupBulkHealthConditionGroup[] {
    const q = this.healthGroupSearch.trim().toLowerCase();
    const list = this.selections.healthConditionTags || [];
    if (!q) return list;
    return list.filter((g) => g.groupName.toLowerCase().includes(q));
  }

  get availableNutrientGroupsForModal(): SupBulkLookupOption[] {
    const q = this.healthModalSearch.trim().toLowerCase();
    const used = new Set((this.selections.healthConditionTags || []).map((g) => String(g.groupId)));
    return (this.lookups.nutrientGroups || [])
      .filter((g) => !used.has(String(g.id)))
      .filter((g) => !q || String(g.name).toLowerCase().includes(q));
  }

  openHealthGroupModal(): void {
    this.healthModalSearch = '';
    this.healthModalSelection = new Set();
    this.healthModalRef = this.modalService.open(this.healthGroupModalTpl, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      windowClass: 'saru-subdialog-window',
    });
    this.healthModalRef.result.catch(() => {
      this.healthModalRef = null;
    });
    this.scheduleLucideIconRender();
  }

  closeHealthGroupModal(): void {
    try {
      this.healthModalRef?.dismiss();
    } catch {
      /* noop */
    }
    this.healthModalRef = null;
  }

  isHealthModalSelected(id: string | number): boolean {
    return this.healthModalSelection.has(String(id));
  }

  toggleHealthModalSelection(id: string | number): void {
    const key = String(id);
    if (this.healthModalSelection.has(key)) this.healthModalSelection.delete(key);
    else this.healthModalSelection.add(key);
  }

  confirmHealthGroups(): void {
    const tags = this.lookups.nutrientTags || [];
    for (const id of this.healthModalSelection) {
      const group = (this.lookups.nutrientGroups || []).find((g) => String(g.id) === id);
      if (!group) continue;
      if ((this.selections.healthConditionTags || []).some((g) => String(g.groupId) === id)) continue;
      const groupTags = tags
        .filter((t) => String(t.groupId) === id)
        .map((t) => ({ id: String(t.id), name: t.name }));
      this.selections.healthConditionTags.push({
        groupId: String(group.id),
        groupName: group.name,
        tags: groupTags,
      });
    }
    this.closeHealthGroupModal();
  }

  removeHealthGroup(group: SupBulkHealthConditionGroup): void {
    this.selections.healthConditionTags = (this.selections.healthConditionTags || []).filter(
      (g) => g.groupId !== group.groupId
    );
  }

  removeHealthTag(group: SupBulkHealthConditionGroup, tagId: string): void {
    group.tags = (group.tags || []).filter((t) => t.id !== tagId);
  }

  getVisibleHealthTags(group: SupBulkHealthConditionGroup): Array<{ id: string; name: string }> {
    const tags = group.tags || [];
    if (this.isHealthExpanded(group.groupId) || tags.length <= 6) return tags;
    return tags.slice(0, 6);
  }

  isHealthExpanded(groupId: string): boolean {
    return this.healthExpanded.has(groupId);
  }

  toggleHealthExpanded(groupId: string): void {
    if (this.healthExpanded.has(groupId)) this.healthExpanded.delete(groupId);
    else this.healthExpanded.add(groupId);
  }

  getClaimTagsForRow(kind: 'product' | 'benefit', index: number): SupBulkLookupOption[] {
    const row =
      kind === 'product'
        ? this.selections.productClaims[index]
        : this.selections.productBenefitClaims[index];
    if (!row) return [];
    if (!row.availableTags) {
      row.availableTags = this.resolveClaimTagsForType(kind, row.claimTypeId);
    }
    return row.availableTags;
  }

  private resolveClaimTagsForType(
    kind: 'product' | 'benefit',
    claimTypeId: string | number | null | undefined
  ): SupBulkLookupOption[] {
    if (claimTypeId == null || claimTypeId === '') return [];
    const typeKey = String(claimTypeId);
    if (kind === 'product') {
      return (this.lookups.claimTags || []).filter(
        (t) => String(t.claimTypeId ?? '') === typeKey && !!String(t.name || '').trim()
      );
    }
    return (this.lookups.productBenefitTags || []).filter(
      (t) => String(t.productBenefitId ?? '') === typeKey && !!String(t.name || '').trim()
    );
  }

  private refreshClaimAvailableTags(kind: 'product' | 'benefit'): void {
    const rows =
      kind === 'product' ? this.selections.productClaims : this.selections.productBenefitClaims;
    (rows || []).forEach((row) => {
      row.availableTags = this.resolveClaimTagsForType(kind, row.claimTypeId);
    });
  }

  onClaimTypeChange(kind: 'product' | 'benefit', index: number): void {
    const row =
      kind === 'product'
        ? this.selections.productClaims[index]
        : this.selections.productBenefitClaims[index];
    if (!row) return;
    const list = kind === 'product' ? this.lookups.claimTypes : this.lookups.productBenefits;
    const match = (list || []).find((o) => String(o.id) === String(row.claimTypeId));
    // Keep claimTypeId as selected (do not re-stringify) — string vs number mismatch
    // makes ng-select re-emit and wipe tag selections.
    row.claimTypeName = match?.name || '';
    row.selectedTagIds = [];
    row.selectedTagNames = [];
    row.availableTags = this.resolveClaimTagsForType(kind, row.claimTypeId);
  }

  onClaimTagsChange(kind: 'product' | 'benefit', index: number): void {
    const row =
      kind === 'product'
        ? this.selections.productClaims[index]
        : this.selections.productBenefitClaims[index];
    if (!row) return;
    const options = row.availableTags?.length
      ? row.availableTags
      : this.resolveClaimTagsForType(kind, row.claimTypeId);
    // Do not reassign selectedTagIds here — a new array reference can reset ng-select.
    row.selectedTagNames = (row.selectedTagIds || [])
      .map((id) => options.find((o) => String(o.id) === String(id))?.name || '')
      .filter(Boolean);
  }

  hasSectionError(section: number): boolean {
    return Object.keys(this.fieldErrors).some((field) => this.fieldSection(field) === section);
  }

  hasFieldError(field: string): boolean {
    return !!this.fieldErrors[field];
  }

  getFieldError(field: string): string {
    return this.fieldErrors[field] || '';
  }

  clearFieldError(field: string): void {
    if (!this.fieldErrors[field]) return;
    delete this.fieldErrors[field];
    this.refreshSectionErrorBanner();
  }

  /** Show error for a single field only after blur / leave focus. */
  onFieldBlur(field: string): void {
    const err = getSupBulkFieldErrors(this.parsed, this.selections).find((e) => e.field === field);
    if (err) {
      this.fieldErrors[field] = err.message;
    } else {
      delete this.fieldErrors[field];
    }
    this.refreshSectionErrorBanner();
  }

  private refreshSectionErrorBanner(): void {
    this.sectionErrorMessages = Object.entries(this.fieldErrors)
      .filter(([field]) => this.fieldSection(field) === this.activeTab)
      .map(([, msg]) => msg);
  }

  private fieldSection(field: string): number {
    const map: Record<string, number> = {
      companyName: 1,
      address: 1,
      executiveOwner: 1,
      mobileNumber: 1,
      email: 1,
      countryId: 1,
      stateId: 1,
      cityId: 1,
      pincodeId: 1,
      brandName: 2,
      productName: 2,
      dietaryPreferenceId: 2,
      productForm: 2,
      dataSourceId: 2,
      databaseSourceId: 2,
      countryOfOriginId: 2,
      recommendedDosageAmount: 7,
      recommendedDosageUnitId: 7,
      assignment: 13,
    };
    return map[field] ?? this.activeTab;
  }

  getParsedText(key: string): string {
    return String((this.parsed as any)?.[key] ?? '');
  }

  setParsedText(key: string, value: string): void {
    (this.parsed as any)[key] = value ?? '';
  }

  private matchOptionByName(
    list: Array<{ id: string | number; name: string }> | undefined,
    name?: string | null
  ): { id: string | number; name: string } | null {
    const needle = String(name || '').trim().toLowerCase();
    if (!needle || !list?.length) return null;
    return list.find((o) => String(o.name || '').trim().toLowerCase() === needle) || null;
  }

  private async hydrateCompanyLocationFromCsvHints(): Promise<void> {
    const pin = String(this.selections.pincode || this.parsed.pincode || '').trim();
    if (pin && !this.selections.pincodeId) {
      try {
        const response = await this.masterService.searchLocationsByPincode(pin, 10);
        const items = this.unwrapList(response).map((item) => this.normalizePincodeLookup(item));
        this.cachePincodeLookups(items);
        const exact =
          items.find((i) => String(i.pincode) === pin) ||
          items.find((i) => String(i.pincode).startsWith(pin)) ||
          items[0];
        if (exact?.countryId && exact?.stateId && exact?.cityId) {
          await this.applyCompanyLocationFromPincode(exact);
          return;
        }
      } catch {
        /* fall through to name cascade */
      }
    }

    if (!this.selections.countryId) {
      const country =
        this.matchOptionByName(this.lookups.countries, this.selections.countryName) ||
        this.matchOptionByName(this.lookups.countries, this.parsed.country);
      if (country) {
        this.selections.countryId = String(country.id);
        this.selections.countryName = country.name;
      }
    }

    if (this.selections.countryId) {
      await this.onCompanyCountryChange(false);
      if (!this.selections.stateId && (this.selections.stateName || this.parsed.state)) {
        const state = this.matchOptionByName(
          this.companyStates,
          this.selections.stateName || this.parsed.state
        );
        if (state) {
          this.selections.stateId = String(state.id);
          this.selections.stateName = state.name;
        }
      }
      if (this.selections.stateId) {
        await this.onCompanyStateChange(false);
        if (!this.selections.cityId && (this.selections.cityName || this.parsed.city)) {
          const city = this.matchOptionByName(
            this.companyCities,
            this.selections.cityName || this.parsed.city
          );
          if (city) {
            this.selections.cityId = String(city.id);
            this.selections.cityName = city.name;
          }
        }
        if (this.selections.cityId) {
          await this.onCompanyCityChange(false);
          if (!this.selections.pincodeId && pin) {
            const pinMatch = this.companyPincodeItems.find(
              (i) => String(i.pincode) === pin || String(i.label).startsWith(pin)
            );
            if (pinMatch) {
              this.selections.pincodeId = String(pinMatch.id);
              this.selections.pincode = pinMatch.pincode || pin;
            }
          }
        }
      }
    }
  }

  private async hydrateEntityLocationFromCsvHints(
    key: keyof Pick<
      SupBulkPreviewRow['selections'],
      | 'manufacturedBy'
      | 'marketedBy'
      | 'packagedBy'
    >
  ): Promise<void> {
    const sel = this.selections[key] as SupBulkEntitySelections;
    if (!sel) return;

    const pin = String(sel.pincode || '').trim();
    if (pin && !sel.pincodeId) {
      try {
        const response = await this.masterService.searchLocationsByPincode(pin, 10);
        const items = this.unwrapList(response).map((item) => this.normalizePincodeLookup(item));
        this.entityPincodeItems[key] = items;
        this.cachePincodeLookups(items);
        const exact =
          items.find((i) => String(i.pincode) === pin) ||
          items.find((i) => String(i.pincode).startsWith(pin)) ||
          items[0];
        if (exact?.countryId && exact?.stateId && exact?.cityId) {
          await this.applyEntityLocationFromPincode(key, exact);
          return;
        }
      } catch {
        /* fall through */
      }
    }

    if (!sel.countryId && sel.countryName) {
      const country = this.matchOptionByName(this.lookups.countries, sel.countryName);
      if (country) {
        sel.countryId = String(country.id);
        sel.countryName = country.name;
      }
    }

    if (sel.countryId) {
      await this.loadEntityStates(key, sel.countryId);
      if (!sel.stateId && sel.stateName) {
        const state = this.matchOptionByName(this.entityStateOptions[key], sel.stateName);
        if (state) {
          sel.stateId = String(state.id);
          sel.stateName = state.name;
        }
      }
      if (sel.stateId) {
        await this.loadEntityCities(key, sel.stateId);
        if (!sel.cityId && sel.cityName) {
          const city = this.matchOptionByName(this.entityCityOptions[key], sel.cityName);
          if (city) {
            sel.cityId = String(city.id);
            sel.cityName = city.name;
          }
        }
        if (sel.cityId) {
          await this.loadEntityPincodes(key, sel.cityId);
          if (!sel.pincodeId && pin) {
            const pinMatch = (this.entityPincodeItems[key] || []).find(
              (i) => String(i.pincode) === pin || String(i.label).startsWith(pin)
            );
            if (pinMatch) {
              sel.pincodeId = String(pinMatch.id);
              sel.pincode = pinMatch.pincode || pin;
            }
          }
        }
      }
    }
  }

  private applyAllValidationErrors(): void {
    const errors = getSupBulkFieldErrors(this.parsed, this.selections);
    this.fieldErrors = {};
    errors.forEach((e) => {
      if (!this.fieldErrors[e.field]) {
        this.fieldErrors[e.field] = e.message;
      }
    });
    this.refreshSectionErrorBanner();
  }

  private unwrapList(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.Data)) return response.Data;
    if (Array.isArray(response?.response)) return response.response;
    if (Array.isArray(response?.Response)) return response.Response;
    const root = response?.response ?? response?.data ?? response ?? {};
    if (Array.isArray(root)) return root;
    return (
      root.items ||
      root.states ||
      root.cities ||
      root.pincodes ||
      root.stateList ||
      root.cityList ||
      root.pincodeList ||
      root.list ||
      []
    );
  }

  private unwrapOptions(response: any): SupBulkLookupOption[] {
    return extractMetadataOptions(this.unwrapList(response))
      .map((o) => ({ id: String(o.id), name: o.name }))
      .filter((o) => o.id && o.name);
  }

  private ensureOption(
    list: SupBulkLookupOption[],
    id?: string | null,
    name?: string | null
  ): SupBulkLookupOption[] {
    if (!id) return list || [];
    const exists = (list || []).some((o) => String(o.id) === String(id));
    if (exists) return list || [];
    return [...(list || []), { id: String(id), name: name || String(id) }];
  }

  private normalizePincodeLookup(raw: any): FpPincodeLookupItem {
    const pincodeId = String(raw?.pincodeId ?? raw?.PincodeId ?? raw?.id ?? '');
    const pincode = String(raw?.pincode ?? raw?.Pincode ?? raw?.name ?? '');
    const cityName = String(raw?.cityName ?? raw?.CityName ?? '');
    const stateName = String(raw?.stateName ?? raw?.StateName ?? '');
    const countryName = String(raw?.countryName ?? raw?.CountryName ?? '');
    const area = String(raw?.area ?? raw?.Area ?? '');
    const areaSuffix = area ? ` (${area})` : '';
    const label =
      raw?.displayLabel ??
      raw?.DisplayLabel ??
      (cityName
        ? `${pincode}${areaSuffix} — ${cityName}, ${stateName}, ${countryName}`
        : `${pincode}${areaSuffix}`);
    return {
      id: pincodeId,
      pincodeId,
      pincode,
      name: pincode || label,
      label,
      area,
      cityId: String(raw?.cityId ?? raw?.CityId ?? ''),
      cityName,
      stateId: String(raw?.stateId ?? raw?.StateId ?? ''),
      stateName,
      countryId: String(raw?.countryId ?? raw?.CountryId ?? ''),
      countryName,
      isLookup: true,
    };
  }

  private cachePincodeLookups(items: FpPincodeLookupItem[]): void {
    items.forEach((item) => {
      const key = String(item.pincodeId || item.id || '');
      if (key) this.pincodeLookupCache.set(key, item);
      if (item.pincode) this.pincodeLookupCache.set(item.pincode, item);
    });
  }

  private initCompanyPincodeTypeahead(): void {
    this.companyPincodeTypeaheadSub = this.companyPincodeTypeahead
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => (term || '').trim().length >= 3),
        switchMap((term) => {
          const query = (term || '').trim();
          this.searchingCompanyPincode = true;
          this.cdRef.markForCheck();
          return from(
            this.masterService
              .searchLocationsByPincode(query, 25)
              .then((response) =>
                this.unwrapList(response).map((item) => this.normalizePincodeLookup(item))
              )
              .catch(() => [] as FpPincodeLookupItem[])
          );
        })
      )
      .subscribe((results) => {
        this.searchingCompanyPincode = false;
        this.companyPincodeItems = results || [];
        this.cachePincodeLookups(this.companyPincodeItems);
        this.cdRef.markForCheck();
      });
  }

  private initEntityPincodeTypeaheads(): void {
    for (const e of this.entityKeys) {
      const key = e.key;
      const subject = new Subject<string>();
      this.entityPincodeTypeahead[key] = subject;
      this.entityPincodeItems[key] = this.entityPincodeItems[key] || [];
      const sub = subject
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          filter((term) => (term || '').trim().length >= 3),
          switchMap((term) => {
            const query = (term || '').trim();
            this.entitySearchingPincode[key] = true;
            this.cdRef.markForCheck();
            return from(
              this.masterService
                .searchLocationsByPincode(query, 25)
                .then((response) =>
                  this.unwrapList(response).map((item) => this.normalizePincodeLookup(item))
                )
                .catch(() => [] as FpPincodeLookupItem[])
            );
          })
        )
        .subscribe((results) => {
          this.entitySearchingPincode[key] = false;
          this.entityPincodeItems[key] = results || [];
          this.cachePincodeLookups(this.entityPincodeItems[key]);
          this.cdRef.markForCheck();
        });
      this.entityPincodeSubs.push(sub);
    }
  }

  async onCompanyCountryChange(resetChildren = true): Promise<void> {
    if (this.applyingPincode) return;
    const countryId = this.selections.countryId;
    const match = this.lookups.countries.find((c) => String(c.id) === String(countryId));
    this.selections.countryName = match?.name || null;
    if (resetChildren) {
      this.selections.stateId = null;
      this.selections.cityId = null;
      this.selections.pincodeId = null;
      this.selections.stateName = null;
      this.selections.cityName = null;
      this.selections.pincode = null;
      this.companyCities = [];
      this.companyPincodeItems = [];
    }
    this.companyStates = [];
    if (!countryId) return;
    this.loadingCompanyStates = true;
    try {
      const res = await this.masterService.getProductStatesByCountry(countryId);
      this.companyStates = this.unwrapOptions(res);
    } catch {
      this.companyStates = [];
    } finally {
      this.loadingCompanyStates = false;
      this.clearFieldError('countryId');
    }
  }

  async onCompanyStateChange(resetChildren = true): Promise<void> {
    if (this.applyingPincode) return;
    const stateId = this.selections.stateId;
    const match = this.companyStates.find((s) => String(s.id) === String(stateId));
    this.selections.stateName = match?.name || null;
    if (resetChildren) {
      this.selections.cityId = null;
      this.selections.pincodeId = null;
      this.selections.cityName = null;
      this.selections.pincode = null;
      this.companyPincodeItems = [];
    }
    this.companyCities = [];
    if (!stateId) return;
    this.loadingCompanyCities = true;
    try {
      const res = await this.masterService.getProductCitiesByState(stateId);
      this.companyCities = this.unwrapOptions(res);
    } catch {
      this.companyCities = [];
    } finally {
      this.loadingCompanyCities = false;
      this.clearFieldError('stateId');
    }
  }

  async onCompanyCityChange(resetChildren = true): Promise<void> {
    if (this.applyingPincode) return;
    const cityId = this.selections.cityId;
    const match = this.companyCities.find((c) => String(c.id) === String(cityId));
    this.selections.cityName = match?.name || null;
    if (resetChildren) {
      this.selections.pincodeId = null;
      this.selections.pincode = null;
    }
    this.companyPincodeItems = [];
    if (!cityId) return;
    this.loadingCompanyPincodes = true;
    try {
      const res = await this.masterService.getProductPincodesByCity(cityId);
      this.companyPincodeItems = this.unwrapList(res).map((item) => {
        const opt = this.normalizePincodeLookup({
          ...item,
          cityId: item.cityId ?? cityId,
          cityName: item.cityName ?? this.selections.cityName,
          stateId: item.stateId ?? this.selections.stateId,
          stateName: item.stateName ?? this.selections.stateName,
          countryId: item.countryId ?? this.selections.countryId,
          countryName: item.countryName ?? this.selections.countryName,
        });
        // City-list items may only have id/name — keep usable label
        if (!opt.pincode) {
          opt.pincode = String(item.name ?? item.pincode ?? '');
          opt.name = opt.pincode;
          opt.label = opt.pincode;
        }
        return opt;
      });
      this.cachePincodeLookups(this.companyPincodeItems);
    } catch {
      this.companyPincodeItems = [];
    } finally {
      this.loadingCompanyPincodes = false;
      this.clearFieldError('cityId');
    }
  }

  onCompanyPincodeOptionSelected(item: any): void {
    if (!item || this.applyingPincode) return;
    const lookup = item.isLookup ? item : this.normalizePincodeLookup(item);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      void this.applyCompanyLocationFromPincode(lookup);
    }
  }

  async onCompanyPincodeSelected(selection: any): Promise<void> {
    if (!selection || this.applyingPincode) return;

    if (typeof selection === 'object') {
      const raw = selection?.value ?? selection;
      const lookupFromItem = raw?.countryId ? raw : this.normalizePincodeLookup(raw);
      if (lookupFromItem?.countryId && lookupFromItem?.stateId && lookupFromItem?.cityId) {
        await this.applyCompanyLocationFromPincode(lookupFromItem);
        return;
      }
      const idFromItem = raw?.pincodeId ?? raw?.id;
      if (idFromItem != null) selection = String(idFromItem);
    }

    const lookup = await this.resolvePincodeLocationLookup(selection);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      await this.applyCompanyLocationFromPincode(lookup);
      return;
    }

    const match = this.companyPincodeItems.find((p) => String(p.id) === String(selection));
    this.selections.pincode = match?.pincode || match?.name || null;
    this.clearFieldError('pincodeId');
  }

  private async resolvePincodeLocationLookup(
    pincodeId: string | number
  ): Promise<FpPincodeLookupItem | null> {
    const key = String(pincodeId).trim();
    if (!key) return null;

    const cached = this.pincodeLookupCache.get(key);
    if (cached?.countryId) return cached;

    const fromItems = this.companyPincodeItems.find(
      (p) => String(p.pincodeId ?? p.id) === key || String(p.pincode) === key
    );
    if (fromItems?.countryId) return fromItems;

    if (!/^\d+$/.test(key)) return null;

    try {
      const response = await this.masterService.getLocationByPincodeId(key);
      const raw =
        response?.pincodeId || response?.PincodeId || response?.pincode
          ? response
          : response?.response ?? response?.data ?? null;
      if (!raw) return null;
      const lookup = this.normalizePincodeLookup(raw);
      if (lookup.countryId) {
        this.pincodeLookupCache.set(String(lookup.pincodeId ?? lookup.id), lookup);
        return lookup;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private async applyCompanyLocationFromPincode(lookup: FpPincodeLookupItem): Promise<void> {
    this.applyingPincode = true;
    try {
      this.lookups.countries = this.ensureOption(
        this.lookups.countries,
        lookup.countryId,
        lookup.countryName
      );

      this.selections.countryId = lookup.countryId;
      this.selections.countryName = lookup.countryName || null;
      this.selections.stateId = lookup.stateId;
      this.selections.stateName = lookup.stateName || null;
      this.selections.cityId = lookup.cityId;
      this.selections.cityName = lookup.cityName || null;
      this.selections.pincodeId = lookup.pincodeId || lookup.id;
      this.selections.pincode = lookup.pincode || null;

      await this.loadCompanyStatesOnly(lookup.countryId);
      this.companyStates = this.ensureOption(this.companyStates, lookup.stateId, lookup.stateName);

      await this.loadCompanyCitiesOnly(lookup.stateId);
      this.companyCities = this.ensureOption(this.companyCities, lookup.cityId, lookup.cityName);

      await this.loadCompanyPincodesOnly(lookup.cityId);
      this.companyPincodeItems = this.mergePincodeItem(this.companyPincodeItems, lookup);
      this.cachePincodeLookups([lookup]);

      this.clearFieldError('countryId');
      this.clearFieldError('stateId');
      this.clearFieldError('cityId');
      this.clearFieldError('pincodeId');
    } finally {
      this.applyingPincode = false;
      this.cdRef.detectChanges();
    }
  }

  private mergePincodeItem(
    list: FpPincodeLookupItem[],
    lookup: FpPincodeLookupItem
  ): FpPincodeLookupItem[] {
    const id = String(lookup.pincodeId || lookup.id);
    const without = (list || []).filter((p) => String(p.id) !== id);
    return [lookup, ...without];
  }

  private async loadCompanyStatesOnly(countryId: string): Promise<void> {
    this.loadingCompanyStates = true;
    try {
      const res = await this.masterService.getProductStatesByCountry(countryId);
      this.companyStates = this.unwrapOptions(res);
    } catch {
      this.companyStates = [];
    } finally {
      this.loadingCompanyStates = false;
    }
  }

  private async loadCompanyCitiesOnly(stateId: string): Promise<void> {
    this.loadingCompanyCities = true;
    try {
      const res = await this.masterService.getProductCitiesByState(stateId);
      this.companyCities = this.unwrapOptions(res);
    } catch {
      this.companyCities = [];
    } finally {
      this.loadingCompanyCities = false;
    }
  }

  private async loadCompanyPincodesOnly(cityId: string): Promise<void> {
    this.loadingCompanyPincodes = true;
    try {
      const res = await this.masterService.getProductPincodesByCity(cityId);
      this.companyPincodeItems = this.unwrapList(res).map((item) =>
        this.normalizePincodeLookup({
          ...item,
          cityId: item.cityId ?? cityId,
          cityName: item.cityName ?? this.selections.cityName,
          stateId: item.stateId ?? this.selections.stateId,
          stateName: item.stateName ?? this.selections.stateName,
          countryId: item.countryId ?? this.selections.countryId,
          countryName: item.countryName ?? this.selections.countryName,
        })
      );
    } catch {
      this.companyPincodeItems = [];
    } finally {
      this.loadingCompanyPincodes = false;
    }
  }

  async onEntityCountryChange(key: string): Promise<void> {
    if (this.applyingPincode) return;
    const sel = (this.selections as any)[key] as SupBulkEntitySelections;
    const match = this.lookups.countries.find((c) => String(c.id) === String(sel.countryId));
    sel.countryName = match?.name || null;
    sel.stateId = null;
    sel.cityId = null;
    sel.pincodeId = null;
    sel.stateName = null;
    sel.cityName = null;
    sel.pincode = null;
    this.entityCityOptions[key] = [];
    this.entityPincodeItems[key] = [];
    await this.loadEntityStates(key, sel.countryId);
  }

  async onEntityStateChange(key: string): Promise<void> {
    if (this.applyingPincode) return;
    const sel = (this.selections as any)[key] as SupBulkEntitySelections;
    const match = (this.entityStateOptions[key] || []).find((s) => String(s.id) === String(sel.stateId));
    sel.stateName = match?.name || null;
    sel.cityId = null;
    sel.pincodeId = null;
    sel.cityName = null;
    sel.pincode = null;
    this.entityPincodeItems[key] = [];
    await this.loadEntityCities(key, sel.stateId);
  }

  async onEntityCityChange(key: string): Promise<void> {
    if (this.applyingPincode) return;
    const sel = (this.selections as any)[key] as SupBulkEntitySelections;
    const match = (this.entityCityOptions[key] || []).find((c) => String(c.id) === String(sel.cityId));
    sel.cityName = match?.name || null;
    sel.pincodeId = null;
    sel.pincode = null;
    await this.loadEntityPincodes(key, sel.cityId);
  }

  onEntityPincodeOptionSelected(key: string, item: any): void {
    if (!item || this.applyingPincode) return;
    const lookup = item.isLookup ? item : this.normalizePincodeLookup(item);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      void this.applyEntityLocationFromPincode(key, lookup);
    }
  }

  async onEntityPincodeSelected(key: string, selection: any): Promise<void> {
    if (!selection || this.applyingPincode) return;

    if (typeof selection === 'object') {
      const raw = selection?.value ?? selection;
      const lookupFromItem = raw?.countryId ? raw : this.normalizePincodeLookup(raw);
      if (lookupFromItem?.countryId && lookupFromItem?.stateId && lookupFromItem?.cityId) {
        await this.applyEntityLocationFromPincode(key, lookupFromItem);
        return;
      }
      const idFromItem = raw?.pincodeId ?? raw?.id;
      if (idFromItem != null) selection = String(idFromItem);
    }

    const fromItems = (this.entityPincodeItems[key] || []).find(
      (p) => String(p.pincodeId ?? p.id) === String(selection)
    );
    if (fromItems?.countryId && fromItems?.stateId && fromItems?.cityId) {
      await this.applyEntityLocationFromPincode(key, fromItems);
      return;
    }

    const lookup = await this.resolvePincodeLocationLookup(selection);
    if (lookup?.countryId && lookup?.stateId && lookup?.cityId) {
      await this.applyEntityLocationFromPincode(key, lookup);
      return;
    }

    const sel = (this.selections as any)[key] as SupBulkEntitySelections;
    sel.pincode = fromItems?.pincode || fromItems?.name || null;
  }

  private async applyEntityLocationFromPincode(
    key: string,
    lookup: FpPincodeLookupItem
  ): Promise<void> {
    this.applyingPincode = true;
    try {
      const sel = (this.selections as any)[key] as SupBulkEntitySelections;
      this.lookups.countries = this.ensureOption(
        this.lookups.countries,
        lookup.countryId,
        lookup.countryName
      );

      sel.countryId = lookup.countryId;
      sel.countryName = lookup.countryName || null;
      sel.stateId = lookup.stateId;
      sel.stateName = lookup.stateName || null;
      sel.cityId = lookup.cityId;
      sel.cityName = lookup.cityName || null;
      sel.pincodeId = lookup.pincodeId || lookup.id;
      sel.pincode = lookup.pincode || null;

      await this.loadEntityStates(key, lookup.countryId);
      this.entityStateOptions[key] = this.ensureOption(
        this.entityStateOptions[key] || [],
        lookup.stateId,
        lookup.stateName
      );

      await this.loadEntityCities(key, lookup.stateId);
      this.entityCityOptions[key] = this.ensureOption(
        this.entityCityOptions[key] || [],
        lookup.cityId,
        lookup.cityName
      );

      await this.loadEntityPincodes(key, lookup.cityId);
      this.entityPincodeItems[key] = this.mergePincodeItem(
        this.entityPincodeItems[key] || [],
        lookup
      );
      this.cachePincodeLookups([lookup]);
    } finally {
      this.applyingPincode = false;
      this.cdRef.detectChanges();
    }
  }

  private async loadEntityStates(key: string, countryId?: string | null): Promise<void> {
    this.entityStateOptions[key] = [];
    if (!countryId) return;
    try {
      const res = await this.masterService.getProductStatesByCountry(countryId);
      this.entityStateOptions[key] = this.unwrapOptions(res);
    } catch {
      this.entityStateOptions[key] = [];
    }
  }

  private async loadEntityCities(key: string, stateId?: string | null): Promise<void> {
    this.entityCityOptions[key] = [];
    if (!stateId) return;
    try {
      const res = await this.masterService.getProductCitiesByState(stateId);
      this.entityCityOptions[key] = this.unwrapOptions(res);
    } catch {
      this.entityCityOptions[key] = [];
    }
  }

  private async loadEntityPincodes(key: string, cityId?: string | null): Promise<void> {
    this.entityPincodeItems[key] = [];
    if (!cityId) return;
    try {
      const res = await this.masterService.getProductPincodesByCity(cityId);
      const sel = (this.selections as any)[key] as SupBulkEntitySelections;
      this.entityPincodeItems[key] = this.unwrapList(res).map((item) =>
        this.normalizePincodeLookup({
          ...item,
          cityId: item.cityId ?? cityId,
          cityName: item.cityName ?? sel?.cityName,
          stateId: item.stateId ?? sel?.stateId,
          stateName: item.stateName ?? sel?.stateName,
          countryId: item.countryId ?? sel?.countryId,
          countryName: item.countryName ?? sel?.countryName,
        })
      );
      this.cachePincodeLookups(this.entityPincodeItems[key]);
    } catch {
      this.entityPincodeItems[key] = [];
    }
  }

  onMasterSelect(
    field:
      | 'dietaryPreferenceId'
      | 'dataSourceId'
      | 'databaseSourceId'
      | 'countryOfOriginId'
      | 'fssaiCategoryId'
      | 'eStoreCategoryId',
    list: SupBulkLookupOption[]
  ): void {
    const id = (this.selections as any)[field];
    const match = (list || []).find((o) => String(o.id) === String(id));
    if (field === 'fssaiCategoryId') this.selections.fssaiCategoryName = match?.name || null;
    if (field === 'eStoreCategoryId') this.selections.eStoreCategoryName = match?.name || null;
    this.clearFieldError(field);
  }

  onContinentChange(): void {
    const match = (this.lookups as any).continents.find((c) => String(c.id) === String((this.selections as any).continentId));
    (this.selections as any).continentName = match?.name || null;
  }

  onParsedChange(field: string): void {
    this.clearFieldError(field);
  }

  addSkuRow(): void {
    this.selections.skuDetails.push({
      name: '',
      netWeight: '',
      mrp: '',
      manufacturedBy: '',
      marketedBy: '',
      packagedBy: '',
      unitId: null,
      unitName: null,
      countryId: null,
      countryName: null,
      productImage: null,
      productBarcode: null,
    });
  }

  removeSkuRow(index: number): void {
    this.selections.skuDetails.splice(index, 1);
  }

  onSkuUnitChange(sku: SupBulkSkuDraft): void {
    const match = this.lookups.units.find((u) => String(u.id) === String(sku.unitId));
    sku.unitName = match?.name || null;
  }

  onSkuCountryChange(sku: SupBulkSkuDraft): void {
    const match = this.lookups.countries.find((c) => String(c.id) === String(sku.countryId));
    sku.countryName = match?.name || null;
  }

  addServingMeasurement(): void {
    void this.openUomDialog();
  }

  removeServingMeasurement(index: number): void {
    (this.selections as any).servingMeasurements.splice(index, 1);
    this.syncUomCategorySelection();
  }

  removeServingMeasurementRow(row: SupBulkServingMeasurement): void {
    const idx = (this.selections as any).servingMeasurements.indexOf(row);
    if (idx >= 0) this.removeServingMeasurement(idx);
  }

  get filteredServingMeasurements(): SupBulkServingMeasurement[] {
    const list = (this.selections as any).servingMeasurements || [];
    if (!this.selectedUomFilter) return list;
    return list.filter(
      (r) => String(r.servingMeasure || r.categoryName || '') === this.selectedUomFilter
    );
  }

  get uomFilterChips(): string[] {
    const names = ((this.selections as any).servingMeasurements || [])
      .map((r) => String(r.servingMeasure || r.categoryName || '').trim())
      .filter(Boolean);
    return Array.from(new Set(names));
  }

  setUomFilter(name: string | null): void {
    this.selectedUomFilter = name;
  }

  async openUomDialog(): Promise<void> {
    if (!this.productUomCategories.length) {
      this.productUomLoading = true;
      try {
        const res: any = await this.masterService.getRecipeUomCategories();
        const list = res?.data || res?.Data || res?.response || [];
        this.productUomCategories = (Array.isArray(list) ? list : []).map((c: any) => ({
          id: Number(c.id ?? c.Id),
          name: String(c.name ?? c.Name ?? ''),
          selected: false,
          adding: false,
        }));
      } catch {
        this.productUomCategories = [];
      } finally {
        this.productUomLoading = false;
      }
    }
    this.syncUomCategorySelection();
    this.uomModalRef = this.modalService.open(this.uomDialogTpl, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      windowClass: 'saru-subdialog-window saru-subdialog-window--uom',
    });
    this.uomModalRef.result.catch(() => {
      this.uomModalRef = null;
    });
    this.scheduleLucideIconRender();
  }

  closeUomDialog(): void {
    try {
      this.uomModalRef?.close();
    } catch {
      /* noop */
    }
    this.uomModalRef = null;
  }

  private syncUomCategorySelection(): void {
    this.productUomCategories.forEach((cat) => {
      cat.selected = ((this.selections as any).servingMeasurements || []).some(
        (row) =>
          String(row.categoryId) === String(cat.id) ||
          row.servingMeasure === cat.name ||
          row.categoryName === cat.name
      );
    });
  }

  async onUomCategoryToggle(cat: {
    id: number;
    name: string;
    selected: boolean;
    adding: boolean;
  }): Promise<void> {
    const willSelect = !cat.selected;
    cat.selected = willSelect;
    if (!willSelect) {
      (this.selections as any).servingMeasurements = ((this.selections as any).servingMeasurements || []).filter(
        (row) =>
          String(row.categoryId) !== String(cat.id) &&
          row.categoryName !== cat.name &&
          row.servingMeasure !== cat.name
      );
      if (this.selectedUomFilter === cat.name) this.selectedUomFilter = null;
      this.cdRef.markForCheck();
      return;
    }
    cat.adding = true;
    try {
      const res: any = await this.masterService.getRecipeUomMasterByCategory(cat.id);
      const rows = res?.data || res?.Data || res?.response || [];
      for (const uom of Array.isArray(rows) ? rows : []) {
        const uomId = String(uom.id ?? uom.Id ?? '');
        if (
          uomId &&
          ((this.selections as any).servingMeasurements || []).some((r) => String(r.uomId) === uomId)
        ) {
          continue;
        }
        const gram = Number(uom.gram ?? uom.Gram ?? 0) || 0;
        const ml = Number(uom.ml ?? uom.Ml ?? 0) || 0;
        (this.selections as any).servingMeasurements.push({
          qty: 1,
          servingSize: String(uom.subcategoryName || uom.SubcategoryName || uom.name || 'Standard'),
          servingMeasure: cat.name,
          amount: gram > 0 ? gram : ml > 0 ? ml : 100,
          unit: gram > 0 ? 'gm' : ml > 0 ? 'ml' : 'gm',
          calories: 0,
          carbs: 0,
          protein: 0,
          fat: 0,
          fiber: 0,
          uomId: uomId || null,
          categoryId: String(uom.categoryId ?? cat.id),
          subcategoryId: uom.subcategoryId != null ? String(uom.subcategoryId) : null,
          categoryName: cat.name,
          subcategoryName: String(uom.subcategoryName || ''),
        });
      }
      this.selectedUomFilter = cat.name;
    } catch (err) {
      console.error('Failed to add UOM category', err);
      cat.selected = false;
    } finally {
      cat.adding = false;
      this.cdRef.markForCheck();
      this.scheduleLucideIconRender();
    }
  }

  getUomCardTheme(
    index: number,
    name: string
  ): { iconKey: string; iconBg: string; iconColor: string; borderColor: string; iconText?: string } {
    const normalized = (name || '').toLowerCase();
    const themed: Array<{
      match: RegExp;
      iconKey: string;
      iconBg: string;
      iconColor: string;
      borderColor: string;
      iconText?: string;
    }> = [
      { match: /soup bowl/, iconKey: 'soup-bowl', iconBg: '#EEF2FF', iconColor: '#4F46E5', borderColor: '#C7D2FE' },
      { match: /bowl|katori|vati/, iconKey: 'bowl', iconBg: '#EEF2FF', iconColor: '#6D35E8', borderColor: '#CDBDFF' },
      { match: /glass/, iconKey: 'glass', iconBg: '#E0F2FE', iconColor: '#0288D1', borderColor: '#98D8FF' },
      { match: /tablespoon|tbsp/, iconKey: 'tablespoon', iconBg: '#FFF7ED', iconColor: '#EA580C', borderColor: '#FDBA74' },
      { match: /teaspoon|tsp/, iconKey: 'teaspoon', iconBg: '#FFF4E5', iconColor: '#F97316', borderColor: '#FFD59A' },
      { match: /cup/, iconKey: 'cup', iconBg: '#FFF7ED', iconColor: '#EA580C', borderColor: '#FED7AA' },
      { match: /slice/, iconKey: 'slice', iconBg: '#FFF1F2', iconColor: '#EC4899', borderColor: '#FBCFE8' },
      { match: /piece/, iconKey: 'piece', iconBg: '#F5F3FF', iconColor: '#7C3AED', borderColor: '#DDD6FE' },
      { match: /serving/, iconKey: 'serving', iconBg: '#F3E8FF', iconColor: '#8B5CF6', borderColor: '#D8B4FE' },
      {
        match: /number/,
        iconKey: 'numbers',
        iconBg: '#F3E8FF',
        iconColor: '#7C3AED',
        borderColor: '#D8B4FE',
        iconText: '123',
      },
      { match: /gram|kg|mg/, iconKey: 'scale', iconBg: '#ECFDF5', iconColor: '#16A34A', borderColor: '#BBF7D0' },
    ];
    const found = themed.find((item) => item.match.test(normalized));
    if (found) return found;
    const fallback = [
      { iconKey: 'bowl', iconBg: '#EEF2FF', iconColor: '#6D35E8', borderColor: '#CDBDFF' },
      { iconKey: 'glass', iconBg: '#E0F2FE', iconColor: '#0288D1', borderColor: '#98D8FF' },
    ];
    return fallback[index % fallback.length];
  }

  getUomCardIconMarkup(theme: {
    iconKey: string;
    iconText?: string;
  }): SafeHtml | string {
    if (theme.iconText) return theme.iconText;
    return this.sanitizer.bypassSecurityTrustHtml(this.getUomCardSvg(theme.iconKey));
  }

  getMeasurementUomIconMarkup(row: SupBulkServingMeasurement): SafeHtml | string {
    const name = row?.servingMeasure || row?.categoryName || '';
    const idx = this.productUomCategories.findIndex((c) => c.name === name);
    return this.getUomCardIconMarkup(this.getUomCardTheme(idx >= 0 ? idx : 0, name));
  }

  getMeasurementUomTheme(row: SupBulkServingMeasurement) {
    const name = row?.servingMeasure || row?.categoryName || '';
    const idx = this.productUomCategories.findIndex((c) => c.name === name);
    return this.getUomCardTheme(idx >= 0 ? idx : 0, name);
  }

  private getUomCardSvg(iconKey: string): string {
    const common =
      'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
    const svgs: Record<string, string> = {
      bowl: `<svg ${common}><path d="M4 11c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M5 11h14"/><path d="M7 17c1.4 1 3.2 1.5 5 1.5s3.6-.5 5-1.5"/></svg>`,
      'soup-bowl': `<svg ${common}><path d="M4 11c0 4.4 3.6 8 8 8s8-3.6 8-8"/><path d="M6 11h12"/><path d="M7 17c1.2.8 2.8 1.2 5 1.2s3.8-.4 5-1.2"/><path d="M8 8c.8-.8 1.7-1.2 2.6-1.2"/></svg>`,
      glass: `<svg ${common}><path d="M7 4h10l-1 16H8L7 4z"/><path d="M8 10h8"/></svg>`,
      cup: `<svg ${common}><path d="M6 8h10v7a4 4 0 0 1-4 4H10a4 4 0 0 1-4-4V8z"/><path d="M16 9h2a2 2 0 0 1 0 4h-1"/></svg>`,
      tablespoon: `<svg ${common}><path d="M10.2 6.2c1.8 1.8 2.3 4.1 1.1 5.3-1.2 1.2-3.5.7-5.3-1.1s-2.3-4.1-1.1-5.3c1.2-1.2 3.5-.7 5.3 1.1Z"/><path d="M12.1 11.4 19 18.3"/></svg>`,
      teaspoon: `<svg ${common}><path d="M10 7c1.4 1.4 1.8 3.1.8 4.1s-2.7.6-4.1-.8-1.8-3.1-.8-4.1 2.7-.6 4.1.8Z"/><path d="M11 11l7.2 7.2"/></svg>`,
      slice: `<svg ${common}><path d="M5 18l14-7-8-6-6 13z"/><path d="M11 5l-2 9"/></svg>`,
      piece: `<svg ${common}><rect x="6" y="6" width="12" height="12" rx="2"/><path d="M10 10h4v4h-4z"/></svg>`,
      serving: `<svg ${common}><path d="M5 12h14"/><path d="M7 8h10l-1 5H8L7 8z"/><path d="M6 12c0 2.7 2.7 5 6 5s6-2.3 6-5"/></svg>`,
      numbers: `<svg ${common}><path d="M7 8h10"/><path d="M7 16h10"/><path d="M9 5v14"/><path d="M15 5v14"/></svg>`,
      scale: `<svg ${common}><path d="M7 6h10"/><path d="M8 6v12h8V6"/><path d="M9.5 10.5h5"/></svg>`,
    };
    return svgs[iconKey] || svgs['bowl'];
  }

  addMacronutrient(): void {
    const row: SupBulkNutrientItem = {
      nutrientId: '',
      nutrientName: '',
      unitId: '',
      unitName: '',
      amount: '',
      percentDV: '',
      rda: '',
    };
    this.selections.macronutrients.push(row);
  }

  removeMacronutrient(index: number): void {
    this.selections.macronutrients.splice(index, 1);
    this.refreshMacroOptions();
    this.scheduleFssaiTagRefresh();
  }

  addClaim(kind: 'product' | 'benefit' | 'ingredient'): void {
    const row: SupBulkClaimDraft = {
      claimTypeId: null,
      claimTypeName: '',
      claimText: '',
      selectedTagIds: [],
      selectedTagNames: [],
      availableTags: [],
      isDone: false,
    };
    if (kind === 'product') this.selections.productClaims.push(row);
    else if (kind === 'benefit') this.selections.productBenefitClaims.push(row);
    else this.selections.ingredientClaims.push(row);
  }

  removeClaim(kind: 'product' | 'benefit' | 'ingredient', index: number): void {
    if (kind === 'product') this.selections.productClaims.splice(index, 1);
    else if (kind === 'benefit') this.selections.productBenefitClaims.splice(index, 1);
    else this.selections.ingredientClaims.splice(index, 1);
  }

  private initIngredientTypeahead(): void {
    this.ingredientTypeaheadSub = this.ingredientTypeahead
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => (term || '').trim().length >= 2),
        switchMap((term) => {
          const query = (term || '').trim();
          this.ingredientSearchLoading = true;
          this.cdRef.markForCheck();
          return from(
            this.nutraceuticalService
              .search(query, 50, true)
              .then((list) =>
                (list || []).map((i) => ({
                  id: Number(i.id),
                  name: String(i.ingredientName || 'Unknown').trim(),
                })).filter((i) => i.id && i.name)
              )
              .catch(() => [] as Array<{ id: number; name: string }>)
          );
        })
      )
      .subscribe((results) => {
        this.ingredientSearchLoading = false;
        this.ingredientSearchResults = results || [];
        this.cdRef.markForCheck();
      });
  }

  private extractIngredientSearchRecords(res: any): any[] {
    const payload =
      res?.data ??
      res?.Data ??
      (res?.response && typeof res.response === 'object' && !Array.isArray(res.response)
        ? res.response.data ?? res.response.Data ?? res.response
        : res?.response) ??
      res;
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    const nested =
      payload.records ??
      payload.Records ??
      payload.items ??
      payload.Items ??
      payload.ingredients ??
      payload.Ingredients ??
      payload.results ??
      payload.Results ??
      payload.list ??
      payload.List ??
      payload.data ??
      payload.Data;
    if (Array.isArray(nested)) return nested;
    for (const key of Object.keys(payload)) {
      if (Array.isArray((payload as any)[key])) return (payload as any)[key];
    }
    return [];
  }

  openIngredientDialog(editIndex?: number): void {
    this.ingredientSearchResults = [];
    this.selectedIngredientId = null;
    this.selectedIngredientName = '';
    this.ingredientServingUnits = [];
    this.selectedServingUnitId = null;
    this.ingredientAmount = null;

    if (editIndex != null && this.selections.ingredients[editIndex]) {
      this.ingredientEditIndex = editIndex;
      const existing = this.selections.ingredients[editIndex];
      this.selectedIngredientId = Number(existing.ingredientId) || null;
      this.selectedIngredientName = existing.name;
      this.selectedServingUnitId = existing.servingUnitMasterId || null;
      this.ingredientAmount = Number(existing.amount) || null;
      if (this.selectedIngredientId) {
        void this.loadIngredientUnits(this.selectedIngredientId);
      }
    } else {
      this.ingredientEditIndex = null;
    }

    this.ingredientModalRef = this.modalService.open(this.ingredientDialogTpl, {
      size: 'md',
      backdrop: 'static',
      centered: true,
      windowClass: 'saru-subdialog-window',
    });
    this.ingredientModalRef.result.catch(() => {
      this.ingredientEditIndex = null;
      this.ingredientModalRef = null;
    });
    this.scheduleLucideIconRender();
  }

  closeIngredientDialog(): void {
    this.ingredientModalRef?.dismiss();
    this.ingredientModalRef = null;
    this.ingredientEditIndex = null;
  }

  async onIngredientPicked(item: any): Promise<void> {
    const id = typeof item === 'object' ? Number(item?.id ?? item?.value) : Number(item);
    if (!id) return;
    this.selectedIngredientId = id;
    const found = this.ingredientSearchResults.find((i) => Number(i.id) === id);
    this.selectedIngredientName = found?.name || this.selectedIngredientName || '';
    this.selectedServingUnitId = null;
    await this.loadIngredientUnits(id);
  }

  private async loadIngredientUnits(_ingredientId: number): Promise<void> {
    this.ingredientServingUnits = (this.lookups.dosageUnits || []).map((u) => ({
      servingUnitMasterId: u.id,
      displayLabel: u.name,
    }));
    this.cdRef.detectChanges();
  }

  saveIngredient(): void {
    if (!this.selectedIngredientId || !this.ingredientAmount || !this.selectedServingUnitId) return;
    const unit = this.ingredientServingUnits.find(
      (u) => String(u.servingUnitMasterId) === String(this.selectedServingUnitId)
    );
    const row: SupBulkResolvedIngredient = {
      ingredientId: String(this.selectedIngredientId),
      name: this.selectedIngredientName || 'Ingredient',
      servingUnitMasterId: String(this.selectedServingUnitId),
      unit: this.formatIngredientUnit(unit?.displayLabel || ''),
      amount: String(this.ingredientAmount),
      isNew: false,
    };
    if (this.ingredientEditIndex != null) {
      this.selections.ingredients[this.ingredientEditIndex] = row;
    } else {
      this.selections.ingredients.push(row);
    }
    this.ingredientModalRef?.close();
    this.ingredientModalRef = null;
    this.ingredientEditIndex = null;
  }

  removeIngredient(index: number): void {
    this.selections.ingredients.splice(index, 1);
  }

  save(): void {
    const errors = getSupBulkFieldErrors(this.parsed, this.selections);
    if (errors.length) {
      this.applyAllValidationErrors();
      this.activeTab = errors[0].section;
      this.refreshSectionErrorBanner();
      this.scheduleLucideIconRender();
      try {
        const body = document.querySelector('.saru-editor__body');
        body?.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        /* noop */
      }
      return;
    }
    this.fieldErrors = {};
    this.sectionErrorMessages = [];
    rebuildSupplementBulkPayload(this.row, this.lookups);
    this.activeModal.close(this.row);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  formatIngredientUnit(raw: string | null | undefined): string {
    const label = String(raw || '').trim();
    if (!label) return '—';
    // "Standard - cup - 149.00 - Gram" -> "cup"
    const parts = label.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const mid = parts[1];
      if (mid && !/^\d+(\.\d+)?$/.test(mid)) return mid;
    }
    return label.length > 28 ? label.slice(0, 26) + '…' : label;
  }

  private scheduleLucideIconRender(): void {
    if (typeof window === 'undefined') return;
    window.setTimeout(() => {
      if ((window as any).lucide?.createIcons) {
        (window as any).lucide.createIcons();
      }
    }, 50);
  }
}
